import { useState, useEffect } from 'react';
import { FriendLocation, User } from '../../../types';
import { useFriendsData } from '../../../contexts/FriendsDataContext';

/**
 * Hook to manage core map state (locations, loading, user position, map center/zoom)
 * Uses FriendsDataContext for caching, but maintains local state for manipulations
 */
export const useMapState = () => {
  // Get cached data from context
  const friendsDataContext = useFriendsData();

  // Local state (initialized from context)
  const [locations, setLocations] = useState<FriendLocation[]>(friendsDataContext.locations);
  const [loading, setLoading] = useState(friendsDataContext.loading);
  const [error, setError] = useState(friendsDataContext.error);

  // Sync with context when it updates
  useEffect(() => {
    setLocations(friendsDataContext.locations);
    setLoading(friendsDataContext.loading);
    setError(friendsDataContext.error);
  }, [friendsDataContext.locations, friendsDataContext.loading, friendsDataContext.error]);

  // Selected friend
  const [selectedFriend, setSelectedFriend] = useState<User | null>(null);
  const [loadingFriend, setLoadingFriend] = useState(false);

  // User geolocation
  const [userPosition, setUserPosition] = useState<[number, number] | null>(null);
  const [geoError, setGeoError] = useState<string>('');

  // Map instance and view
  const [mapInstance, setMapInstance] = useState<any>(null);
  const [mapCenter, setMapCenter] = useState<[number, number]>([48.8566, 2.3522]); // Paris default
  const [mapZoom, setMapZoom] = useState(6);

  // Highlighted friend (for search results)
  const [highlightedFriendId, setHighlightedFriendId] = useState<number | null>(null);

  return {
    // Friend locations (synced with context)
    locations,
    setLocations,
    loading,
    setLoading,
    error,
    setError,
    // Context methods for loading data
    friendsDataContext,

    // Selected friend
    selectedFriend,
    setSelectedFriend,
    loadingFriend,
    setLoadingFriend,

    // Geolocation
    userPosition,
    setUserPosition,
    geoError,
    setGeoError,

    // Map control
    mapInstance,
    setMapInstance,
    mapCenter,
    setMapCenter,
    mapZoom,
    setMapZoom,

    // Highlighted friend
    highlightedFriendId,
    setHighlightedFriendId,
  };
};
